import _ from "lodash"
import ArticleCollection from "../catalog/ArticleCollection"
import DomainFactory from "../lib/DomainFactory"
import Base from "./Base"
import Group, { ComposableElement } from "./Group"
import Cluster from "./Cluster"
import Pendant from "./Pendant"
import Table from '../data/Table'
import Composition from "./Composition"
import { baseAttribute, DomainModelsData } from "./composition"
import Toggles from "@domain/toggles/Toggles"
import { UnsupportedCompositionOperationError } from "./CompositionOperationError"

export interface SpaceElement<T extends Base = Base> {
	element: T,
	options: {
		position: number[]
	}
}

export interface RoomConfig {
	width: number
	height: number
	depth: number
	extraCeiling?: number
	ceilingMaterial?: string
}
export interface VibiaSpaceConfig {
	roomSize?: RoomConfig
}

// A stateless composition operation. The consumer (ApiHub/LumenAI) holds the composition
// and sends an operation to apply; the domain owns what each operation type means.
export interface CompositionOperation {
	type: string
	params?: any
}

export type Configurator = 'COMPOSITOR' | 'ATTRIBUTES_CONFIGURATOR' | 'EXPRESS' | "CREA"
export type Source = 'PUBLIC_AREA' | 'PRIVATE_AREA'
export class Space extends Group {
	config: VibiaSpaceConfig;
	articleCollection: ArticleCollection;
	configurator: Configurator;
	productId: string;
	source: Source
	type = 'Space'
	static className = 'Space'

	constructor(
		elements: SpaceElement[] | Group,
		config?: VibiaSpaceConfig,
		parent?,
		articleCollection?: ArticleCollection,
		id?,
		attributes?: any,
		productId?: string,
		configurator?: Configurator,
		source?: Source
	) {
		super(elements, parent, null, id, attributes)
		this.config = config
		this.articleCollection = articleCollection
		if (configurator) {
			this.configurator = configurator
		}
		if (source) {
			this.source = source
		}
		if (productId) {
			this.productId = productId
		}

		if (Toggles.isEnabled("logDomainConstructor")) {
			console.log(`%c[Space]`, '🐋;background: lightcyan; color: #444;padding: 3px; border-radius: 5px;', '🐋')
		}
	}

	getArticleCollection(): ArticleCollection {
		return this.articleCollection
	}

	getElements() {
		return super.getElements() as SpaceElement[]
	}

	getElement(element: Base | string): SpaceElement {
		return super.getElement(element) as SpaceElement
	}

	setProductId(productId: string) {
		this.productId = productId
	}

	getProductId() {
		return this.productId
	}

	addElement(newSpaceElement: SpaceElement | Base, prevId?: string) {
		return new Space(
			super.addElement(newSpaceElement, prevId),
			this.getConfig(),
			this.getParent(),
			this.getArticleCollection(),
			this.getId(),
			null,
			this.getProductId(),
			this.getConfigurator(),
			this.getSource()
		)
	}

	moveElement(element: Base, x, y, z, requestedByComposition?: boolean) {
		let _element: SpaceElement = this.getElement(element) ?? element.getParent().getElement(element) as SpaceElement

		if (requestedByComposition)
			/* @ts-ignore */
			_element = (this.getElement(element.getParent()).element as Composition).getElement(element)
		if (!_element)
			throw new Error('No element to move found')



		// if (this.config && this.config.roomSize) {
		// 	posX = Math.min((this.config.roomSize.depth / 2) - (_element.element.getSize().depth / 2), posX)
		// 	posZ = Math.min((this.config.roomSize.width / 2) - (_element.element.getSize().width / 2), posZ)
		// 	posX = Math.max(posX, (_element.element.getSize().depth / 2) - (this.config.roomSize.depth / 2))
		// 	posZ = Math.max(posZ, (_element.element.getSize().width / 2) - (this.config.roomSize.width / 2))
		// }

		// Cluster + Cluster + Composition + Cluster + Composition
		// replaces Composition descendants by its Cluster descendants
		var descendants = []
		this.getElements().forEach(descendant => {
			const type = new DomainFactory().getType(descendant)
			if (type == 'Composition') { // FIXME: COMPOSITION
				(descendant.element as Composition).getElements().forEach((elem) => {
					descendants.push(elem)

					if (elem.element.getClassName() == 'Cluster') {
						if ((elem.element as Cluster).isFreeWire())
							descendants.push(...(elem.element as Cluster).getNestedElements())
					}
				})
			}
			else {
				descendants.push(descendant)

				if (type == 'Cluster') {
					if ((descendant.element as Cluster).isFreeWire())
						descendants.push(...(descendant.element as Cluster).getNestedElements())
				}
			}
		})

		const absoluteX = _element.element.getAbsolutePosition()[0] + x
		const absoluteZ = _element.element.getAbsolutePosition()[2] + z
		// check collide against other lamps
		var collides = this.checkCollides(_element, absoluteX, absoluteZ, descendants, [x, y, z])

		//if there is a collission, mark the object
		_element.element.setAttribute('collides', collides, true)
		_element.element.setAttribute('collides', collides)


		var posX = _element.options.position[0] + x
		var posZ = _element.options.position[2] + z

		const tmpX = _element.options.position[0]
		const tmpZ = _element.options.position[2]
		try {
			_element.options.position[0] = posX
			_element.options.position[2] = posZ
			this.requestValidate()
			if (!collides) {
				_element.element.setAttribute('lastValidPosition', [tmpX, _element.options.position[1], tmpZ], true)
				_element.element.setAttribute('lastValidPosition', [tmpX, _element.options.position[1], tmpZ])
			}
		} catch (e) {
			//rollback
			_element.options.position[0] = tmpX
			_element.options.position[2] = tmpZ
		}

		return element
	}

	rotate2DElement(element: Base, rotationCenterX, rotationCenterZ, angle, requestedByComposition?: boolean): this {
		let _element: SpaceElement = this.getElement(element)

		if (requestedByComposition)
			/* @ts-ignore */
			_element = (this.getElement(element.getParent()).element as Composition).getElement(element)
		if (!_element)
			throw new Error('No element to move found')

		let absolutePositionElement = _element.element.getAbsolutePosition();

		var radians = (Math.PI / 180) * angle,
			cos = Math.cos(radians),
			sin = Math.sin(radians),
			nx = (cos * (absolutePositionElement[0] - rotationCenterX)) + (sin * (absolutePositionElement[2] - rotationCenterZ)) + rotationCenterX,
			nz = (cos * (absolutePositionElement[2] - rotationCenterZ)) - (sin * (absolutePositionElement[0] - rotationCenterX)) + rotationCenterZ;

		var descendants = []
		this.getElements().forEach(descendant => {
			const type = new DomainFactory().getType(descendant)
			if (type == 'Composition') // FIXME: COMPOSITION
				(descendant.element as Composition).getElements().forEach(elem => descendants.push(elem))
			else
				descendants.push(descendant)
		})

		var collides = this.checkCollides(_element, nx, nz, descendants)

		_element.element.setAttribute('collides', collides, true)
		_element.element.setAttribute('collides', collides)

		_element.options.position[0] = nx
		_element.options.position[2] = nz

		return this;
	}

	private checkCollides(_element: SpaceElement, posX: any, posZ: any, descendants: any[], movement?: number[]) {
		const colliders = _element.element.getColliders([posX, _element.options.position[1], posZ])
		const type = new DomainFactory().getType((_element as ComposableElement))
		let isInsideRoom: boolean = true

		if (type == 'Cluster') {
			if ((_element.element as Cluster).isFreeWire()) {
				(_element.element as Cluster).getNestedElements().map((ele, i) => {
					const final_pos_pendant = [ele.element.getAbsolutePosition()[0] + movement[0], 0, ele.element.getAbsolutePosition()[2] + movement[2]]
					colliders.push(...ele.element.getColliders(final_pos_pendant))

					isInsideRoom = !this.config || !this.config.roomSize
						? false
						: (final_pos_pendant[0] > (ele.element.getSize().depth / 2) - (this.config.roomSize.depth / 2) &&
							final_pos_pendant[0] < (this.config.roomSize.depth / 2) - (ele.element.getSize().depth / 2) &&
							final_pos_pendant[2] < (this.config.roomSize.width / 2) - (ele.element.getSize().width / 2) &&
							final_pos_pendant[2] > (ele.element.getSize().width / 2) - (this.config.roomSize.width / 2))
				})
			}
		}

		isInsideRoom = !isInsideRoom || !this.config || !this.config.roomSize
			? false
			: (posX > (_element.element.getSize().depth / 2) - (this.config.roomSize.depth / 2) &&
				posX < (this.config.roomSize.depth / 2) - (_element.element.getSize().depth / 2) &&
				posZ < (this.config.roomSize.width / 2) - (_element.element.getSize().width / 2) &&
				posZ > (_element.element.getSize().width / 2) - (this.config.roomSize.width / 2))

		var collides = !isInsideRoom

		if (collides)
			return true

		colliders.forEach((collider) => {
			descendants.forEach((element) => {
				if (element.element.getId() != _element.element.getId() &&
					element.element.getParent()?.getId() != _element.element.getId()) {
					const secondColliders = element.element.getColliders(element.element.getAbsolutePosition())
					secondColliders.forEach(secondCollider => {
						if (collider.collides(secondCollider)) {
							collides = true
						}
					})
				}
			})
		})
		return collides
	}

	toString() {
		return JSON.stringify(this.getElements(), null, 4)
	}

	setConfig(config: VibiaSpaceConfig) {
		this.config = config

		// Make sure all Light childrens have valid attributes (namely pendantHeight & installation)
		this.deleteInvalidAttributes()
		this.resetInvalidAttributes()
	}

	// Composition operation vocabulary. The domain owns which operations exist and what each
	// one does; consumers (ApiHub/LumenAI) only forward an operation and never inspect the
	// vocabulary. An unknown operation raises a typed error so callers can map it to their own
	// contract (e.g. a 4xx) without re-implementing the list of valid operations.
	applyOperation(operation: CompositionOperation): Space {
		switch (operation.type) {
			case 'setRoomConfig': {
				this.setConfig(operation.params)
				return this
			}
			default: {
				throw new UnsupportedCompositionOperationError(operation.type)
			}
		}
	}

	getConfig() {
		return this.config
	}

	getSource(): Source {
		return this.source
	}

	getConfigurator(): Configurator {
		return this.configurator
	}

	setConfigurator(configurator: Configurator): Space {
		this.configurator = configurator
		return this
	}

	requestSetAttribute(attribute: baseAttribute, value, element: Base) {
		const hasRoom = !!(this.config && this.config.roomSize)
		if (attribute == 'pendantHeight') {
			const pendant = element as Pendant
			const pos = pendant.getAbsolutePosition()
			const clusterHeight = pos && pos[1] ? -1 * pendant.getAbsolutePosition()[1] : 0
			let maxPendantHeight
			if (hasRoom) {
				maxPendantHeight = this.config.roomSize.height - 0.10
				if (value > maxPendantHeight) {
					return maxPendantHeight
				}
				if (value < clusterHeight) {
					return clusterHeight
				}
			}
		}
		if (attribute == 'installation' && value) {
			const requestedCanopy = Table.getCanopy(value)
			if (hasRoom && requestedCanopy) { // We know the room restrictions for this canopy -> Check if room allows it.
				// HACK: Fallback for room restrictions is always installation '10' instead of
				// dynamically calculating appropiate fallback. This works because all 'Composing Pendants' models
				// have installation '10' as default, and without room restrictions.
				// Check extraCeiling
				const roomExtraCeiling = this.config.roomSize.extraCeiling || 0
				const requiredExtraCeiling = requestedCanopy.extraCeiling || 0
				if (roomExtraCeiling < requiredExtraCeiling) {
					return '10 Surface Basic'
				}
				// Check ceiling material
				const roomCeilingMaterial = this.config.roomSize.ceilingMaterial
				const allowedCeilingMaterials = requestedCanopy.compatibleMaterials || []
				const allAllowed = allowedCeilingMaterials.find(mat => mat == 'all')

				const currentMaterialAllowed = allowedCeilingMaterials.find(mat => mat == roomCeilingMaterial)
				if (!allAllowed && !currentMaterialAllowed) {
					return '10 Surface Basic'
				}
			}
		}
		value = super.requestSetAttribute(attribute, value, element)
		return value;
	}

	removeAutoadded() {
		const compositions = this.getCompositions()
		compositions.forEach(composition => {
			composition.removeAutoadded()
		})
		
		super.removeAutoadded()
		return this
	}

	getInfo() {
		return {
			...super.getInfo(),
			type: 'space'
		}
	}

	toJSON() {
		const productId = this.getProductId()

		return {
			...super.toJSON(),
			config: this.config,
			configurator: this.configurator,
			source: this.source,
			...(productId ? { productId } : {})

			// CONTRACT: articleCollection is intentionally NOT serialized into the composition.
			// It is an injected dependency (stock/availability/discontinued data) that belongs
			// near the order, not inside the composition JSON that travels to the consumer. It is
			// supplied out-of-band to validate/price. If it ever needs to be serialized, it must be
			// an explicit, optional field — never coupled into the round-trip payload.
		}
	}

	fromJSON(json, options?: any, instance?: Base, familyData?: DomainModelsData) {
		return Space.fromJSON(json, options, instance, familyData)
	}

	static fromJSON(json: any, options?: { articleCollection?: ArticleCollection } & any, instance?: Base, familyData?: DomainModelsData) {
		const domainFactory = new DomainFactory(familyData)
		const group = Group.fromJSON(
			json,
			{ domainFactory },
			null,
		)
		const space = super.fromJSON(
			json,
			{ domainFactory },
			instance ?? new Space(
				group, // elements
				json.config, // config
				null, // parent
				options?.articleCollection ?? json.articleCollection, // articleCollection
				null, // id
				null, // attributes
				json?.productId, // productId
				json?.configurator, // configurator
				json?.source // source
			)
		) as Space

		return space
	}

	static dataFactory(data?, children?: any[]) {
		data = data ?? {}
		return {
			"type": "Space",
			"props": {
				"attributes": {},
				//@ts-ignore
				"elements": children ?? []
			},
			"virtual": null,
			"config": {
				"roomSize": {
					"height": 4,
					"width": 3,
					"depth": 3,
					"extraCeiling": 0.16,
					"ceilingMaterial": "pladur",
					...data
				}
			},
			"configurator": "COMPOSITOR",
			"source": "PRIVATE_AREA",
		}
	}

	// Builds an empty, valid composition ready to be mutated by a consumer (ApiHub/LumenAI).
	// It reuses dataFactory (default room, COMPOSITOR configurator, PRIVATE_AREA source) and
	// re-instantiates through fromJSON, so the returned Space is guaranteed to round-trip:
	// Space.empty().toJSON() is re-instantiable with Space.fromJSON().
	// No familyData is needed while the composition is empty; it becomes a hard dependency only
	// once real elements are added (see DomainFactory / getSubfamilyDataById).
	static empty(roomConfig?: Partial<RoomConfig>): Space {
		return Space.fromJSON(Space.dataFactory(roomConfig)) as Space
	}
}

export default Space